import { useCallback, useEffect, useMemo } from 'react';

import {
  ValidationItems,
  useFormContext,
  useWatch,
} from '@kanda-libs/ks-component-ts';

import { useCurrentJob } from 'hooks';
import { FIELD_NAMES } from 'pages/Quote/constants';

import { BorrowAmountClassNames, CLASS_NAMES } from './constants';
import { checkLower, checkUpper } from './helpers';

import { DepositLimits, formatter } from '../../../helpers';

export interface BorrowAmountHook {
  error: string | undefined;
  validation: ValidationItems;
  classNames: BorrowAmountClassNames;
  depositWarningIsDueToCreditLine: boolean;
  loanAmount: number;
  onBlur: () => void;
  steps: string;
}

function useBorrowAmount(
  depositLimits: DepositLimits,
  min: string,
  max: string,
  creditLine: number,
): BorrowAmountHook {
  const { job } = useCurrentJob();

  const jobTotal = job?.total?.amount_vat_inclusive?.amount || 0;

  const [editLoanAmount, loanAmount, provider, error] = useWatch({
    name: [
      FIELD_NAMES.editLoanAmount,
      FIELD_NAMES.loanAmount,
      FIELD_NAMES.provider,
      FIELD_NAMES.error,
    ],
  });

  const { setValue, getValues } = useFormContext();

  const validation: ValidationItems = {
    validate: {
      value: {
        lower: () =>
          checkLower(getValues(FIELD_NAMES.loanAmount), depositLimits.min),
        upper: () =>
          checkUpper(getValues(FIELD_NAMES.loanAmount), depositLimits.max),
      },
      message: {
        lower: `Deposit must be above ${formatter(depositLimits.min)}`,
        upper: `Deposit must be below ${formatter(depositLimits.max)}`,
      },
    },
  };

  const onBlur = useCallback(() => {
    if (editLoanAmount < parseInt(min, 10)) {
      setValue(
        FIELD_NAMES.error,
        'The borrow amount is less than the minimum for this rate, please adjust to proceed with the application.',
      );
      return;
    }
    if (editLoanAmount > parseInt(max, 10)) {
      setValue(
        FIELD_NAMES.error,
        'The borrow amount is more than the maximum for this rate, please adjust to proceed with the application.',
      );
      return;
    }
    setValue(FIELD_NAMES.editLoanAmount, String(editLoanAmount));
    setValue(FIELD_NAMES.loanAmount, editLoanAmount);
    setValue(FIELD_NAMES.depositAmount, jobTotal - editLoanAmount);
    setValue(FIELD_NAMES.error, undefined);
  }, [editLoanAmount, setValue, jobTotal, min, max]);

  const classNames = useMemo(
    () => ({
      ...CLASS_NAMES,
      field: [CLASS_NAMES.field].filter(Boolean).join(' '),
      button: [CLASS_NAMES.button].filter(Boolean).join(' '),
    }),
    [],
  );

  const depositWarningIsDueToCreditLine =
    provider === 'etika' && creditLine !== 0 && creditLine <= jobTotal;

  const steps = useMemo(
    () => String(parseInt(max, 10) - parseInt(min, 10)),
    [min, max],
  );

  // Update the depositAmount and editLoanAmount when loanAmount changes
  useEffect(() => {
    setValue(FIELD_NAMES.depositAmount, String(jobTotal - loanAmount));
    setValue(FIELD_NAMES.editLoanAmount, jobTotal - (jobTotal - loanAmount));
    setValue(FIELD_NAMES.error, undefined);
  }, [loanAmount, jobTotal, setValue]);

  return {
    error,
    loanAmount,
    validation,
    classNames,
    depositWarningIsDueToCreditLine,
    onBlur,
    steps,
  };
}

export default useBorrowAmount;
